import React, { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { ShieldCheck, Clock, Star, Phone, Award, Users, ThumbsUp, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '@/components/Footer';
import GlobalBanner from '@/components/GlobalBanner';
import PageSEO from '@/components/PageSEO';
import { CityPageLinks } from '@/components/InternalLinks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// All service areas with their slugs
const SERVICE_AREAS = [
  { name: 'New Orleans', slug: 'new-orleans' },
  { name: 'Metairie', slug: 'metairie' },
  { name: 'Kenner', slug: 'kenner' },
  { name: 'Slidell', slug: 'slidell' },
  { name: 'Chalmette', slug: 'chalmette' },
  { name: 'Covington', slug: 'covington' },
  { name: 'Mandeville', slug: 'mandeville' },
  { name: 'Hammond', slug: 'hammond' },
  { name: 'Gretna', slug: 'gretna' },
  { name: 'Marrero', slug: 'marrero' },
];

// City data configuration
const CITY_DATA = {
  'new-orleans': {
    name: 'New Orleans',
    state: 'LA',
    headline: 'New Orleans',
    tagline: 'The Big Easy\'s Most Trusted HVAC Pros',
    description: 'Serving the French Quarter, Garden District, Uptown, Mid-City, and all New Orleans neighborhoods.',
  },
  'metairie': {
    name: 'Metairie',
    state: 'LA', 
    headline: 'Metairie',
    tagline: 'Jefferson Parish\'s Top-Rated HVAC Service',
    description: 'Fast, reliable HVAC service for Metairie, Old Metairie, Bucktown, and surrounding areas.',
  },
  'kenner': {
    name: 'Kenner',
    state: 'LA',
    headline: 'Kenner',
    tagline: 'Your Local Kenner HVAC Experts',
    description: 'Serving Kenner, Rivertown, and areas near Louis Armstrong Airport.',
  },
  'slidell': {
    name: 'Slidell',
    state: 'LA',
    headline: 'Slidell',
    tagline: 'St. Tammany Parish\'s Trusted HVAC Team',
    description: 'Expert HVAC service for Slidell, Eden Isles, Oak Harbor, and the Northshore.',
  },
  'chalmette': {
    name: 'Chalmette',
    state: 'LA',
    headline: 'Chalmette',
    tagline: 'St. Bernard Parish\'s HVAC Specialists',
    description: 'Reliable heating and cooling for Chalmette, Arabi, Meraux, and St. Bernard Parish.',
  },
  'covington': {
    name: 'Covington',
    state: 'LA',
    headline: 'Covington',
    tagline: 'Northshore\'s Premier HVAC Service',
    description: 'Quality HVAC solutions for Covington, Downtown, and the greater Northshore area.',
  },
  'mandeville': {
    name: 'Mandeville',
    state: 'LA',
    headline: 'Mandeville',
    tagline: 'Lakefront Living Deserves Quality HVAC',
    description: 'Expert service for Mandeville, Old Mandeville, and Lake Pontchartrain communities.',
  },
  'hammond': {
    name: 'Hammond',
    state: 'LA',
    headline: 'Hammond',
    tagline: 'Tangipahoa Parish\'s HVAC Professionals',
    description: 'Serving Hammond, Ponchatoula, and the Tangipahoa Parish community.',
  },
  'gretna': {
    name: 'Gretna',
    state: 'LA',
    headline: 'Gretna',
    tagline: 'West Bank\'s Reliable HVAC Service',
    description: 'Fast, affordable HVAC for Gretna, Harvey, Terrytown, and the West Bank.',
  },
  'marrero': {
    name: 'Marrero',
    state: 'LA',
    headline: 'Marrero',
    tagline: 'Jefferson Parish West Bank HVAC Experts',
    description: 'Quality heating and cooling for Marrero, Westwego, and surrounding communities.',
  },
};

const CityLandingPage = () => {
  const { city } = useParams();
  const cityData = CITY_DATA[city];

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    zip: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect to home if city not found
  if (!cityData) {
    return <Navigate to="/" replace />;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const scrollToForm = () => {
    const element = document.getElementById('city-lead-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.zip) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/leads`, {
        name: formData.name,
        phone: formData.phone,
        email: `${formData.zip}@nolead.local`, // Placeholder since email is required
        service_type: 'general-inquiry',
        message: `Lead from ${cityData.name} landing page. ZIP: ${formData.zip}`
      });
      if (response.data.success) {
        toast.success('Thank you! We\'ll call you within 30 minutes!');
        setFormData({ name: '', phone: '', zip: '' });
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <PageSEO citySlug={city} breadcrumbs={[
        { name: 'Home', url: '/' },
        { name: `HVAC Services ${cityData.name}`, url: `/hvac/${city}` },
      ]} />
      <GlobalBanner />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0EA5E9] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">N</span>
            </div>
            <span className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              NOLA HVAC Connect
            </span>
          </a>
          <button
            onClick={scrollToForm}
            className="hidden md:flex items-center gap-2 bg-[#F97316] text-white px-4 py-2 rounded-full font-bold hover:bg-[#EA580C] transition-colors cursor-pointer"
          >
            Get Free Quote
          </button>
        </div>
      </header>

      {/* Hero Section with Form Above the Fold */}
      <section className="bg-gradient-to-br from-slate-50 to-white py-8 md:py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#E0F2FE] text-[#0EA5E9] px-4 py-2 rounded-full text-sm font-medium mb-4">
                <ShieldCheck className="w-4 h-4" />
                <span>Serving {cityData.name}, {cityData.state}</span>
              </div>

              <h1
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] leading-tight mb-3"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                #1 HVAC Service in
                <br />
                <span className="text-[#F97316]">{cityData.headline}</span>
              </h1>

              <p className="text-lg text-slate-600 mb-3">
                {cityData.tagline}
              </p>
              
              <p className="text-slate-500 mb-6">
                {cityData.description}
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-[#0EA5E9]" />
                  <span className="text-sm text-slate-600 font-medium">Licensed & Insured</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#F97316]" />
                  <span className="text-sm text-slate-600 font-medium">4.9 Star Rating</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#0EA5E9]" />
                  <span className="text-sm text-slate-600 font-medium">Same-Day Service</span>
                </div>
              </div>

              {/* Mobile CTA */}
              <button
                onClick={scrollToForm}
                className="lg:hidden inline-flex items-center gap-2 bg-[#F97316] text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-[#EA580C] transition-colors cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>

            {/* Right Content - Simplified Form */}
            <div id="city-lead-form" className="bg-white p-6 md:p-8 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100">
              <h2
                className="text-2xl font-bold text-[#0F172A] mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Get Your Free Quote
              </h2>
              <p className="text-slate-500 mb-6">
                {cityData.name}'s fastest HVAC response. We'll call you within 30 minutes!
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-slate-700 font-medium">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-1.5 h-12 border-slate-200 focus:border-[#0EA5E9] rounded-lg"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-slate-700 font-medium">
                    Phone *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="mt-1.5 h-12 border-slate-200 focus:border-[#0EA5E9] rounded-lg"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="zip" className="text-slate-700 font-medium">
                    Zip Code *
                  </Label>
                  <Input
                    id="zip"
                    name="zip"
                    type="text"
                    placeholder="70115"
                    value={formData.zip}
                    onChange={handleInputChange}
                    className="mt-1.5 h-12 border-slate-200 focus:border-[#0EA5E9] rounded-lg"
                    maxLength={5}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg transition-all"
                >
                  {isSubmitting ? 'Submitting...' : `Get My Free Quote`}
                </Button>

                <p className="text-center text-sm text-slate-500">
                  ✓ No spam ✓ Free estimates ✓ No obligation
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-[#0EA5E9]" />
              </div>
              <p className="text-3xl font-bold text-[#0F172A]">5,000+</p>
              <p className="text-sm text-slate-500">Happy Customers</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mx-auto mb-3">
                <ThumbsUp className="w-6 h-6 text-[#0EA5E9]" />
              </div>
              <p className="text-3xl font-bold text-[#0F172A]">15+</p>
              <p className="text-sm text-slate-500">Years Experience</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mx-auto mb-3">
                <Award className="w-6 h-6 text-[#0EA5E9]" />
              </div>
              <p className="text-3xl font-bold text-[#0F172A]">100%</p>
              <p className="text-sm text-slate-500">Satisfaction</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mx-auto mb-3">
                <Clock className="w-6 h-6 text-[#0EA5E9]" />
              </div>
              <p className="text-3xl font-bold text-[#0F172A]">24/7</p>
              <p className="text-sm text-slate-500">Emergency Service</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 
            className="text-3xl font-bold text-center text-[#0F172A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            HVAC Services in {cityData.name}
          </h2>
          <p className="text-slate-500 text-center mb-12 max-w-2xl mx-auto">
            From emergency AC repairs to new system installations, our certified technicians are ready to help.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'AC Repair & Installation', desc: `Fast AC service for ${cityData.name} homes. Same-day repairs available.` },
              { title: 'Heating Services', desc: 'Furnace repair, heat pump installation, and heating maintenance.' },
              { title: 'Emergency 24/7', desc: `Round-the-clock emergency HVAC service in ${cityData.name}.` },
            ].map((service, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-bold text-[#0F172A] mb-2">{service.title}</h3>
                <p className="text-slate-500 mb-4">{service.desc}</p>
                <button onClick={scrollToForm} className="text-[#F97316] font-semibold hover:underline cursor-pointer">
                  Get Quote →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Need HVAC Service in {cityData.name}?
          </h2>
          <p className="text-slate-300 mb-8">
            Get connected with a licensed HVAC professional today. Free estimates, no obligation.
          </p>
          <button
            onClick={scrollToForm}
            className="inline-flex items-center gap-2 bg-[#F97316] text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-[#EA580C] transition-colors cursor-pointer"
          >
            Get Free Quote
          </button>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 
              className="text-3xl font-bold text-[#0F172A] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Service Areas We Cover
            </h2>
            <p className="text-slate-500">
              Professional HVAC services throughout the Greater New Orleans area
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {SERVICE_AREAS.map((area) => (
              <a
                key={area.slug}
                href={`/hvac/${area.slug}`}
                className={`flex items-center gap-2 p-4 rounded-xl border transition-all ${
                  area.slug === city 
                    ? 'bg-[#0EA5E9] text-white border-[#0EA5E9]' 
                    : 'bg-white text-slate-700 border-slate-200 hover:border-[#0EA5E9] hover:shadow-md'
                }`}
              >
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium text-sm">HVAC Services {area.name}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <CityPageLinks cityName={cityData.name} />

      <Footer />
    </div>
  );
};

export default CityLandingPage;
